import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import ToggleButton from '@mui/material/ToggleButton'
import { useEffect, useState } from 'react'

/**
 * ToggleFilters - Customizable filters for tables
 *
 * @param {Array} buttonControls - Array of button configuration objects. Schema: [{label: string, onClick: callable, children?: Any}, ...]
 * @param {string} defaultValue - The initially selected button label
 * @param {Object} buttonStyle - Style configuration for individual buttons
 * @param {Object} groupStyle - Style configuration for the ToggleButtonGroup
 *
 * @returns {JSX.Element} A group of toggle buttons for filtering or selection
 *
 * @example
 * const filterButtons = [
 *   { label: 'All', onClick: noArgsCallback },
 *   { label: 'Active', onClick: (event, value) => withArgsCallback(event, value) },
 *   { label: 'Completed', onClick: () => handleFilter('completed') , children: <SomeIcon />}
 * ];
 *
 * <ToggleFilters
 *   buttonControls={filterButtons}
 *   defaultValue="All"
 *   buttonStyle={{ sx: { borderRadius: 2 } }}
 *   groupStyle={{ size: 'medium' }}
 * />
 */
const ToggleFilters = ({ buttonControls, defaultValue, reset, buttonStyle = {}, groupStyle = {} }) => {
  const [activeButton, setActiveButton] = useState(defaultValue || '')
  const groupProps = {
    exclusive: groupStyle.exclusive || true,
    size: groupStyle.size || 'small',
    color: groupStyle.color || 'primary',
  }

  const buttonProps = {
    sx: buttonStyle.sx || {
      textTransform: 'none',
      fontSize: 14,
    },
    color: buttonStyle.color || 'primary',
  }

  const handleChange = (event, value) => {
    const selectedButton = buttonControls.find((button) => button.label === value)
    setActiveButton(selectedButton.label)
    if (!selectedButton.onClick) {
      return
    }
    selectedButton.onClick(event, value)
  }

  useEffect(() => {
    setActiveButton(defaultValue || '')
  }, [reset])

  return (
    <ToggleButtonGroup value={activeButton} onChange={handleChange} {...groupProps}>
      {buttonControls.map((button) => {
        return (
          <ToggleButton key={button.label} value={button.label} {...buttonProps}>
            {button.children || button.label}
          </ToggleButton>
        )
      })}
    </ToggleButtonGroup>
  )
}

export default ToggleFilters
